import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Heart,
  Send,
  RotateCcw,
  FileText,
  MessageSquare,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { VideoPlayer } from '../components/VideoPlayer';
import { LessonCard } from '../components/LessonCard';
import { Button } from '../components/ui/Button';
import { Textarea } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { DifficultyBadge } from '../components/DifficultyBadge';
import { useTagColors, tagChipStyle } from '../lib/tagColors';
import { Spinner } from '../components/ui/Spinner';
import { fmtDuration, timeAgo } from '../lib/utils';

export default function LessonDetail() {
  const { lessonId } = useParams();
  const tagColors = useTagColors();
  const navigate = useNavigate();
  const { setUser, user: me } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('about');
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [editId, setEditId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const posRef = useRef(0);

  const loadLesson = useCallback(() => {
    setLoading(true);
    api
      .get(`/lessons/${lessonId}`)
      .then(({ data }) => setLesson(data.lesson))
      .catch(() => toast.error('Lesson not found'))
      .finally(() => setLoading(false));
  }, [lessonId]);

  const loadComments = useCallback(() => {
    api.get(`/lessons/${lessonId}/comments`, { params: { sort: 'top' } }).then(({ data }) => setComments(data.comments || []));
  }, [lessonId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadLesson();
    loadComments();
  }, [loadLesson, loadComments]);

  const saveProgress = useCallback(
    (position, completed = false) => {
      posRef.current = position;
      api
        .post(`/lessons/${lessonId}/progress`, { position_seconds: position, completed })
        .then(({ data }) => {
          if (data.user) setUser(data.user);
        })
        .catch(() => {});
    },
    [lessonId, setUser]
  );

  const onTime = useCallback(
    (position, ended) => {
      saveProgress(position, ended || false);
    },
    [saveProgress]
  );

  const markComplete = () => {
    saveProgress(posRef.current || lesson?.duration_seconds || 0, true);
    setLesson((l) => ({ ...l, completed: true }));
    toast.success('Lesson marked complete', { icon: '✓' });
  };

  const resetProgress = () => {
    api.post(`/lessons/${lessonId}/progress/reset`).then(() => {
      setLesson((l) => ({ ...l, completed: false, progress_seconds: 0 }));
      toast('Progress reset — rewatch anytime');
    });
  };

  const toggleBookmark = () => {
    api.post(`/bookmarks/${lessonId}`).then(({ data }) => {
      setLesson((l) => ({ ...l, bookmarked: data.bookmarked }));
    });
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!commentBody.trim()) return;
    setPosting(true);
    try {
      await api.post(`/lessons/${lessonId}/comments`, { body: commentBody.trim() });
      setCommentBody('');
      loadComments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not post comment');
    } finally {
      setPosting(false);
    }
  };

  const likeComment = (id) => {
    api.post(`/comments/${id}/like`).then(loadComments);
  };

  const postReply = async (parentId) => {
    if (!replyBody.trim()) return;
    try {
      await api.post(`/lessons/${lessonId}/comments`, { body: replyBody.trim(), parent_id: parentId });
      setReplyBody('');
      setReplyTo(null);
      loadComments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reply');
    }
  };

  const saveEdit = async (id) => {
    if (!editBody.trim()) return;
    try {
      await api.put(`/comments/${id}`, { body: editBody.trim() });
      setEditId(null);
      setEditBody('');
      loadComments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not edit comment');
    }
  };

  const deleteComment = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      loadComments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete comment');
    }
  };

  if (loading)
    return (
      <div className="grid h-[60vh] place-items-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  if (!lesson) return null;

  const topLevel = comments.filter((c) => !c.parent_id);
  const repliesFor = (id) => comments.filter((c) => c.parent_id === id);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white" data-testid="lesson-back">
        <ChevronLeft size={16} /> Back
      </button>

      <VideoPlayer
        poster={lesson.thumbnail_url}
        resumeAt={lesson.progress_seconds || 0}
        onTime={onTime}
        playbackUrl={lesson.playback_url}
        embedUrl={lesson.embed_url}
        locked={false}
      />

      <div className="flex flex-wrap items-center gap-2">
        <DifficultyBadge level={lesson.difficulty} />
        {(lesson.tags || []).map((t) => {
          const style = tagChipStyle(tagColors[t]);
          return (
            <span
              key={t}
              style={style || undefined}
              className={style
                ? 'rounded-full border px-2 py-0.5 text-[11px] font-medium'
                : 'rounded-full border border-emerald/30 bg-emerald/10 px-2 py-0.5 text-[11px] font-medium text-emerald'}
            >
              {t}
            </span>
          );
        })}
        <span className="tnum text-xs text-zinc-500">• {fmtDuration(lesson.duration_seconds)}</span>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <h1 className="font-heading text-2xl font-bold leading-tight tracking-tight text-white lg:text-3xl">
          {lesson.title}
        </h1>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant={lesson.bookmarked ? 'emerald' : 'outline'}
            size="sm"
            onClick={toggleBookmark}
            data-testid="bookmark-btn"
          >
            <Bookmark size={16} className={lesson.bookmarked ? 'fill-current' : ''} />
            {lesson.bookmarked ? 'Saved' : 'Save'}
          </Button>
          {lesson.completed ? (
            <Button variant="outline" size="sm" onClick={resetProgress} data-testid="reset-progress-btn">
              <RotateCcw size={16} /> Rewatch
            </Button>
          ) : (
            <Button variant="emerald" size="sm" onClick={markComplete} data-testid="mark-complete-btn">
              <CheckCircle2 size={16} /> Mark complete
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10">
        {[
          ['about', 'About', FileText],
          ['comments', `Comments${topLevel.length ? ` (${topLevel.length})` : ''}`, MessageSquare],
        ].map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`lesson-tab-${key}`}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm transition-colors ${
              tab === key ? 'border-emerald text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'about' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <p className="leading-relaxed text-zinc-300">{lesson.description}</p>
          {lesson.notes && (
            <div className="rounded-2xl border border-white/5 bg-ink-card p-6">
              <h3 className="mb-2 font-heading font-semibold text-white">Notes</h3>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-400">{lesson.notes}</p>
            </div>
          )}
          {Array.isArray(lesson.resources) && lesson.resources.length > 0 && (
            <div className="rounded-2xl border border-white/5 bg-ink-card p-6">
              <h3 className="mb-3 font-heading font-semibold text-white">Resources</h3>
              <div className="space-y-2">
                {lesson.resources.map((r, i) => (
                  <a
                    key={i}
                    href={r.url || '#'}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-emerald hover:underline"
                  >
                    <FileText size={14} /> {r.title || r.name || r.url}
                  </a>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {tab === 'comments' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <form onSubmit={postComment} className="space-y-3">
            <Textarea
              rows={3}
              placeholder="Share a thought or ask a question…"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              data-testid="comment-input"
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" variant="emerald" disabled={posting} data-testid="comment-submit">
                <Send size={15} /> Post
              </Button>
            </div>
          </form>

          <div className="space-y-5">
            {topLevel.length === 0 && <p className="text-sm text-zinc-500">Be the first to comment.</p>}
            {topLevel.map((c) => (
              <div key={c.comment_id} className="space-y-3" data-testid={`comment-${c.comment_id}`}>
                <CommentRow
                  c={c}
                  me={me}
                  onLike={likeComment}
                  onReply={() => { setReplyTo(replyTo === c.comment_id ? null : c.comment_id); setReplyBody(''); }}
                  onEdit={() => { setEditId(c.comment_id); setEditBody(c.body); }}
                  onDelete={() => deleteComment(c.comment_id)}
                  editing={editId === c.comment_id}
                  editBody={editBody}
                  setEditBody={setEditBody}
                  onSaveEdit={() => saveEdit(c.comment_id)}
                  onCancelEdit={() => setEditId(null)}
                />
                {replyTo === c.comment_id && (
                  <div className="ml-10 flex gap-2">
                    <Textarea rows={2} placeholder="Write a reply…" value={replyBody} onChange={(e) => setReplyBody(e.target.value)} data-testid={`reply-input-${c.comment_id}`} />
                    <Button size="sm" variant="emerald" onClick={() => postReply(c.comment_id)} data-testid={`reply-submit-${c.comment_id}`}>Reply</Button>
                  </div>
                )}
                {repliesFor(c.comment_id).map((r) => (
                  <div key={r.comment_id} className="ml-10">
                    <CommentRow
                      c={r}
                      me={me}
                      onLike={likeComment}
                      onEdit={() => { setEditId(r.comment_id); setEditBody(r.body); }}
                      onDelete={() => deleteComment(r.comment_id)}
                      editing={editId === r.comment_id}
                      editBody={editBody}
                      setEditBody={setEditBody}
                      onSaveEdit={() => saveEdit(r.comment_id)}
                      onCancelEdit={() => setEditId(null)}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Prev / next */}
      <div className="grid gap-3 pt-4 sm:grid-cols-2">
        {lesson.prev_lesson && (
          <button
            onClick={() => navigate(`/app/lessons/${lesson.prev_lesson.lesson_id}`)}
            className="flex items-center gap-3 rounded-2xl border border-white/5 bg-ink-card p-4 text-left transition-colors hover:border-white/20"
            data-testid="prev-lesson-btn"
          >
            <ChevronLeft className="text-zinc-500" />
            <div className="min-w-0">
              <div className="text-xs text-zinc-500">Previous</div>
              <div className="truncate font-medium text-white">{lesson.prev_lesson.title}</div>
            </div>
          </button>
        )}
        {lesson.next_lesson && (
          <button
            onClick={() => navigate(`/app/lessons/${lesson.next_lesson.lesson_id}`)}
            className="flex items-center justify-end gap-3 rounded-2xl border border-white/5 bg-ink-card p-4 text-right transition-colors hover:border-white/20 sm:col-start-2"
            data-testid="next-lesson-btn"
          >
            <div className="min-w-0">
              <div className="text-xs text-zinc-500">Up next</div>
              <div className="truncate font-medium text-white">{lesson.next_lesson.title}</div>
            </div>
            <ChevronRight className="text-zinc-500" />
          </button>
        )}
      </div>

      {Array.isArray(lesson.similar_lessons) && lesson.similar_lessons.length > 0 && (
        <div className="space-y-4 pt-6">
          <h2 className="font-heading text-xl font-semibold text-white">Similar lessons</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {lesson.similar_lessons.slice(0, 3).map((l, i) => (
              <LessonCard key={l.lesson_id} lesson={l} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CommentRow({ c, me, onLike, onReply, onEdit, onDelete, editing, editBody, setEditBody, onSaveEdit, onCancelEdit }) {
  const mine = me && (c.user_id === me.user_id || c.user?.user_id === me.user_id);
  return (
    <div className="flex gap-3">
      <div
        className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-ink-elevated text-sm text-white"
        style={c.user?.level ? { border: `1px solid ${c.user.level.color}55` } : {}}
      >
        {c.user?.picture ? (
          <img src={c.user.picture} alt="" className="h-full w-full object-cover" />
        ) : (
          (c.user?.name || 'U').charAt(0).toUpperCase()
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">{c.user?.name || 'User'}</span>
          {c.user?.is_admin && <Badge tone="emerald">Admin</Badge>}
          {c.pinned && <Badge tone="amber">Pinned</Badge>}
          <span className="text-xs text-zinc-600">{timeAgo(c.created_at)}{c.edited ? ' · edited' : ''}</span>
        </div>

        {editing ? (
          <div className="mt-2 space-y-2">
            <Textarea rows={2} value={editBody} onChange={(e) => setEditBody(e.target.value)} data-testid={`edit-input-${c.comment_id}`} />
            <div className="flex gap-2">
              <Button size="sm" variant="emerald" onClick={onSaveEdit} data-testid={`edit-save-${c.comment_id}`}>Save</Button>
              <Button size="sm" variant="ghost" onClick={onCancelEdit}>Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{c.body}</p>
        )}

        {!editing && (
          <div className="mt-2 flex items-center gap-4 text-xs">
            <button
              onClick={() => onLike(c.comment_id)}
              data-testid={`comment-like-${c.comment_id}`}
              className={`flex items-center gap-1.5 transition-colors ${c.liked_by_me ? 'text-emerald' : 'text-zinc-500 hover:text-white'}`}
            >
              <Heart size={13} className={c.liked_by_me ? 'fill-current' : ''} /> {c.likes_count || 0}
            </button>
            {onReply && (
              <button onClick={onReply} data-testid={`comment-reply-${c.comment_id}`} className="text-zinc-500 hover:text-white">Reply</button>
            )}
            {mine && (
              <>
                <button onClick={onEdit} data-testid={`comment-edit-${c.comment_id}`} className="text-zinc-500 hover:text-white">Edit</button>
                <button onClick={onDelete} data-testid={`comment-delete-${c.comment_id}`} className="text-zinc-500 hover:text-red-400">Delete</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
